import React from 'react'
import NavbarOne from './separate/NavbarOne'
import FooterHome from './separate/FooterHome'
import { Link, useNavigate } from 'react-router-dom'
import {
  Handshake,
  Globe,
  TrendingUp,
  Users,
  Shield,
  Zap,
  Mail,
  ArrowRight,
  Building2,
  Rocket,
  Award,
  HeartHandshake,
} from 'lucide-react'
import './Partnership.css'
import PageTitleDes from '../core/support/PageTitleDes'

const partnershipTypes = [
  {
    icon: Building2,
    title: 'Institutional Partners',
    description:
      'For exchanges, funds, and financial institutions seeking deep liquidity and white-label solutions.',
    benefits: [
      'Custom API integration',
      'Dedicated account manager',
      'Priority support 24/7',
    ],
  },
  {
    icon: Globe,
    title: 'Regional Partners',
    description:
      "Expand Unitic's presence in your region and earn lifetime commissions on referred users.",
    benefits: [
      'Up to 50% revenue share',
      'Marketing support',
      'Localized resources',
    ],
  },
  {
    icon: Rocket,
    title: 'Technology Partners',
    description:
      "Integrate your solutions with Unitic's ecosystem and reach millions of traders worldwide.",
    benefits: [
      'API access & documentation',
      'Co-marketing opportunities',
      'Technical support',
    ],
  },
  {
    icon: HeartHandshake,
    title: 'Community Partners',
    description:
      'For influencers, educators, and community leaders passionate about crypto adoption.',
    benefits: [
      'Exclusive referral rates',
      'Early feature access',
      'Community rewards',
    ],
  },
]

const stats = [
  { value: 'Trusted', label: 'Partner Network' },
  { value: 'Global', label: 'Market Access' },
  { value: 'Strong', label: 'Liquidity Support' },
  { value: 'High', label: 'Availability Infra' },
]

const benefits = [
  {
    icon: TrendingUp,
    title: 'Revenue Growth',
    description:
      'Competitive commission structures with lifetime earnings potential',
  },
  {
    icon: Shield,
    title: 'Trusted Platform',
    description: 'Industry-leading security and compliance standards',
  },
  {
    icon: Users,
    title: 'Dedicated Support',
    description: 'Personal partnership managers and priority assistance',
  },
  {
    icon: Zap,
    title: 'Fast Integration',
    description: 'Comprehensive APIs and documentation for quick setup',
  },
  {
    icon: Award,
    title: 'Recognition',
    description: 'Partner badges, certifications, and exclusive events',
  },
  {
    icon: Globe,
    title: 'Global Reach',
    description: 'Access to millions of users across 180+ countries',
  },
]

export default function Partnership(props) {
  const navigate = useNavigate()
  return (
    <div className='partnership-page'>
       <PageTitleDes title={`Crypto Partnership Program | Business Collaboration Opportunities`} description={`Partner with us to explore strategic crypto business opportunities. Collaborate, grow, and scale your business with our trusted exchange platform.`} />
      <NavbarOne setTheme={props.setTheme} theme={props.theme} />

      {/* Hero Section */}
      <section
        className='position-relative overflow-hidden d-flex justify-content-center align-items-center py-5'
        style={{ height: '100%',minHeight: 'calc(88vh - 10px)' }}
      >
        {/* Animated Background */}
        <div>
        <div
          className='position-absolute top-0 start-0 w-100 h-100'
          style={{ pointerEvents: 'none' }}
        >
          <div
            className='hero-bg-glow animate-pulse-glow'
            style={{
              top: '25%',
              left: '25%',
              width: '24rem',
              height: '24rem',
              background: 'rgba(29, 160, 148, 0.1)',
            }}
          />
          <div
            className='hero-bg-glow animate-pulse-glow'
            style={{
              bottom: '25%',
              right: '25%',
              width: '20rem',
              height: '20rem',
              background: 'rgba(29, 160, 148, 0.05)',
              animationDelay: '1s',
            }}
          />

          {/* Floating Partnership Icons */}
          <div
            className='position-absolute animate-float'
            style={{ top: '10rem', left: '10%', opacity: 0.2 }}
          >
            <Handshake size={64} className='text-primary-custom' />
          </div>
          <div
            className='position-absolute animate-float'
            style={{
              top: '15rem',
              right: '15%',
              opacity: 0.2,
              animationDelay: '2s',
            }}
          >
            <Globe size={48} className='text-primary-custom' />
          </div>
          <div
            className='position-absolute animate-float'
            style={{
              bottom: '10rem',
              left: '20%',
              opacity: 0.2,
              animationDelay: '1s',
            }}
          >
            <Users size={56} className='text-primary-custom' />
          </div>
        </div>

        <div className='container position-relative z-1'>
          <div className='text-center mx-auto' style={{ maxWidth: '56rem' }}>
            {/* Badge */}
            <div
              className='d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill mb-4 animate-fade-in'
              style={{
                background: 'rgba(29, 160, 148, 0.1)',
                border: '1px solid rgba(29, 160, 148, 0.2)',
              }}
            >
              <Handshake size={16} className='text-primary-custom' />
              <span
                className='text-primary-custom'
                style={{ fontSize: '0.875rem', fontWeight: 500 }}
              >
                Partner With Us
              </span>
            </div>

            <h1
              className='display-3 fw-bold mb-4 animate-slide-up'
              style={{ color: '#e6edf3' }}
            >
              Grow Together with <span className='gradient-text'>Unitic</span>
            </h1>

            <p
              className='lead text-muted-foreground mb-5 mx-auto animate-slide-up'
              style={{ maxWidth: '42rem', animationDelay: '0.1s' }}
            >
              Join our global network of partners and unlock new revenue streams
              while shaping the future of cryptocurrency trading.
            </p>

            <div
              className='d-flex flex-column flex-sm-row gap-3 justify-content-center animate-slide-up'
              style={{ animationDelay: '0.2s' }}
            >
              <div onClick={() => navigate('/help-center')} className='btn-hero'>
                <Mail size={20} className='me-2' />
                Contact Us
                <ArrowRight size={16} className='ms-2' />
              </div>
              <Link to='/sub-broker' className='btn-hero-outline'>
                Sub-Broker Program
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div
            className='row row-cols-2 row-cols-lg-4 g-3 mt-5 mx-auto'
            style={{ maxWidth: '62rem' }}
          >
            {stats.map((stat, index) => (
              <div key={stat.label} className='col'>
                <div
                  className='stat-card animate-slide-up'
                  style={{ animationDelay: `${0.3 + index * 0.1}s` }}
                >
                  <div className='h3 fw-bold gradient-text mb-2'>
                    {stat.value}
                  </div>
                  <div className='small text-muted-foreground'>
                    {stat.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        </div>
      </section>

      {/* Partnership Types */}
      <section className='pt-5 position-relative' style={{ paddingBottom:'90px'}}>
        <div className='container'>
          <div className='text-center mb-5'>
            <h2 className='fw-bold partnerShip-section-title mb-3'>
              Partnership Programs
            </h2>
            <p
              className='text-muted-foreground mx-auto'
              style={{ maxWidth: '42rem' }}
            >
              Choose the partnership model that fits your business and start
              earning today
            </p>
          </div>

          <div
            className='row row-cols-1 row-cols-md-2 g-4 mx-auto mt-5'
            style={{ maxWidth: '64rem' }}
          >
            {partnershipTypes.map((type, index) => (
              <div key={type.title} className='col'>
                <div
                  className='partner-card p-4 p-md-5'
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className='icon-box'>
                    <type.icon size={28} className='text-primary-custom' />
                  </div>

                  <h3 className='h4 fw-bold mb-3' style={{ color: '#e6edf3' }}>
                    {type.title}
                  </h3>

                  <p className='text-muted-foreground mb-4'>
                    {type.description}
                  </p>

                  <ul className='list-unstyled mb-0 d-flex flex-column gap-3'>
                    {type.benefits.map((benefit) => (
                      <li
                        key={benefit}
                        className='d-flex align-items-center gap-3 small'
                      >
                        <div
                          style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: '#1da094',
                          }}
                        />
                        <span className='text-muted-foreground'>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section
        style={{ background: 'rgba(255,255,255,0.02)', paddingTop:'80px', paddingBottom:'90px' }}
      >
        <div className='container'>
          <div className='text-center mb-5'>
            <h2 className='fw-bold mb-3 partnerShip-section-title'>
              Why Partner with Unitic?
            </h2>
            <p
              className='text-muted-foreground mx-auto'
              style={{ maxWidth: '42rem' }}
            >
              Join a platform trusted by millions with industry-leading
              technology and support
            </p>
          </div>

          <div
            className='row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-4 mx-auto mt-5'
            style={{ maxWidth: '72rem' }}
          >
            {benefits.map((benefit, index) => (
              <div key={benefit.title} className='col'>
                <div className='benefit-card'>
                  <div className='benefit-icon-box'>
                    <benefit.icon size={24} className='text-primary-custom' />
                  </div>
                  <h3 className='h5 fw-bold mb-2' style={{ color: '#e6edf3' }}>
                    {benefit.title}
                  </h3>
                  <p className='small text-muted-foreground mb-0'>
                    {benefit.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className='position-relative overflow-hidden' style={{paddingTop:'80px', paddingBottom:'90px'}}>
        <div
          className='position-absolute top-50 start-50 translate-middle'
          style={{
            width: '600px',
            height: '600px',
            background: 'rgba(29, 160, 148, 0.05)',
            borderRadius: '50%',
            filter: 'blur(60px)',
            pointerEvents: 'none',
          }}
        />

        <div className='container position-relative z-1'>
          <div className='mx-auto text-center' style={{ maxWidth: '48rem' }}>
            <div
              className='d-flex align-items-center justify-content-center mx-auto mb-4 animate-pulse-glow'
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(29, 160, 148, 0.1)',
              }}
            >
              <Mail size={40} className='text-primary-custom' />
            </div>

            <h2 className='fw-bold mb-4 partnerShip-section-title'>
              Ready to Partner?
            </h2>

            <p className='lead text-muted-foreground mb-5'>
              Get in touch with our partnership team to discuss opportunities
              and start your journey with Unitic.
            </p>

            <div
              className='glass-card p-5 mx-auto'
              style={{ maxWidth: '36rem' }}
            >
              <div className='d-flex align-items-center justify-content-center gap-2 mb-4'>
                <Mail size={20} className='text-primary-custom' />
                <span
                  className='h5 mb-0 fw-medium'
                  style={{ color: '#e6edf3' }}
                >
                  Contact Email
                </span>
              </div>

              <div
                className='d-block gradient-text fw-bold mb-4 text-decoration-none vip-cta-email'
              >
                supports@uniticexchange.com
              </div>

              <button
                onClick={() => navigate('/help-center')}
                className='btn-hero w-100'
              >
                Send Partnership Inquiry
                <ArrowRight size={16} className='ms-2' />
              </button>
            </div>
          </div>
        </div>
      </section>

      <FooterHome />
    </div>
  )
}
